// Bloc « Synchronisation » de l'inspecteur et des saisies de terrain : opérations enregistrées sur cet appareil
// et leur état, envoi à l'ouverture, au retour du réseau et à la demande, contrôle d'intégrité de la file.
// Les états affichés sont ceux du cadrage (section 9) ; rien n'est dit « synchronisé » avant l'accusé.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Indisponible, MessageErreur } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import {
  horsLigneDisponible,
  LIBELLES_ETATS_OPERATION,
  LIBELLES_TYPES_OPERATIONS,
  operationsLocales,
  synchroniser,
  verifierFile,
  type EtatOperation,
} from './horsligne';

/** État du réseau vu par le navigateur, mis à jour aux événements « online » et « offline ». */
export function useEnLigne(): boolean {
  const [enLigne, setEnLigne] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const maj = () => setEnLigne(navigator.onLine);
    window.addEventListener('online', maj);
    window.addEventListener('offline', maj);
    return () => {
      window.removeEventListener('online', maj);
      window.removeEventListener('offline', maj);
    };
  }, []);
  return enLigne;
}

export const CLE_OPERATIONS = ['horsligne', 'operations'] as const;

export function useOperationsLocales() {
  return useQuery({
    queryKey: CLE_OPERATIONS,
    queryFn: async () => {
      const [operations, integrite] = await Promise.all([operationsLocales(), verifierFile()]);
      return { operations, integrite };
    },
    enabled: horsLigneDisponible(),
    staleTime: 0,
  });
}

const EN_ATTENTE: EtatOperation[] = ['enregistre_local', 'en_attente_envoi', 'envoye'];

function classeEtat(etat: EtatOperation): string {
  if (etat === 'refuse') return 'etiquette rouge';
  if (etat === 'conflit' || EN_ATTENTE.includes(etat)) return 'etiquette ambre';
  return 'etiquette';
}

export function BlocSynchronisation({ compact = false }: { compact?: boolean }) {
  const client = useQueryClient();
  const enLigne = useEnLigne();
  const locales = useOperationsLocales();
  const envoi = useMutation({
    mutationFn: synchroniser,
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: CLE_OPERATIONS });
      await client.invalidateQueries({ queryKey: ['inspection'] });
      await client.invalidateQueries({ queryKey: ['inspections'] });
      await client.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
  const operations = locales.data?.operations ?? [];
  const enAttente = operations.filter((o) => EN_ATTENTE.includes(o.etat));
  const aTraiter = operations.filter((o) => o.etat === 'conflit' || o.etat === 'refuse');
  const { mutate, isPending } = envoi;

  // Reprise automatique : à l'ouverture et à chaque retour du réseau, s'il reste des opérations à envoyer.
  useEffect(() => {
    if (enLigne && enAttente.length > 0 && !isPending) mutate();
    // Seuls le retour du réseau et l'apparition d'opérations en attente déclenchent l'envoi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enLigne, enAttente.length]);

  if (!horsLigneDisponible()) {
    return (
      <section className="carte-bloc" aria-labelledby="titre-synchro">
        <h2 id="titre-synchro">Synchronisation</h2>
        <Indisponible motif="ce navigateur ne permet pas le stockage local chiffré (navigation privée ou stockage bloqué) : la saisie exige le réseau" />
      </section>
    );
  }

  const recentes = [...operations].reverse().slice(0, compact ? 4 : 12);
  return (
    <section className="carte-bloc" aria-labelledby="titre-synchro">
      <div className="carte-bloc-tete">
        <h2 id="titre-synchro">Synchronisation</h2>
        <span className={enLigne ? 'point-etat' : 'point-etat ambre'}>{enLigne ? 'Réseau disponible' : 'Hors connexion'}</span>
      </div>
      {locales.data && !locales.data.integrite.intacte && (
        <p className="message erreur" role="alert">
          Des enregistrements locaux ont été effacés par le système de l’appareil ({locales.data.integrite.perdus.length}). Vérifiez dans vos missions ce que le serveur a reçu et ressaisissez ce qui manque.
        </p>
      )}
      {operations.length === 0 ? (
        <p className="attenue">Aucune saisie en attente sur cet appareil. Les saisies faites sans réseau y sont enregistrées chiffrées, puis envoyées dès le retour du réseau.</p>
      ) : (
        <>
          <dl className="paires">
            <div>
              <dt>En attente d’envoi</dt>
              <dd>{enAttente.length}</dd>
            </div>
            <div>
              <dt>Conflits ou refus à examiner</dt>
              <dd>{aTraiter.length}</dd>
            </div>
            <div>
              <dt>Synchronisées</dt>
              <dd>{operations.filter((o) => o.etat === 'synchronise').length}</dd>
            </div>
          </dl>
          <ul className="liste-doublons">
            {recentes.map((o) => (
              <li key={o.identifiantClient}>
                <span>
                  <b>{LIBELLES_TYPES_OPERATIONS[o.type] ?? o.type}</b>
                  <small>
                    Saisi le {dateHeure(o.horodatageEvenement)}
                    {o.motif ? `, ${o.motif}` : ''}
                  </small>
                </span>
                <span className={classeEtat(o.etat)}>{LIBELLES_ETATS_OPERATION[o.etat]}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Envoi impossible pour l’instant" />}
      {envoi.data?.interrompu && (
        <p className="message avertissement" role="status">
          Envoi interrompu : {envoi.data.interrompu}. Les enregistrements restent en attente d’envoi.
        </p>
      )}
      {envoi.data && envoi.data.envoyees > 0 && (
        <p className="attenue">
          Dernier envoi : {envoi.data.synchronisees} reçue(s) par le serveur, {envoi.data.conflits} conflit(s), {envoi.data.refusees} refus.
        </p>
      )}
      {enAttente.length > 0 && (
        <button type="button" className="bouton primaire" disabled={!enLigne || envoi.isPending} onClick={() => envoi.mutate()}>
          {envoi.isPending ? 'Envoi en cours' : 'Synchroniser maintenant'}
        </button>
      )}
    </section>
  );
}
